import {createSlice} from "@reduxjs/toolkit";
import moment from "moment/moment";

const currentDate = moment().toDate();
const currentYear = moment().year();

const newInstallationInitialValue = {
    type: "",
    address: "",
    doy: "",
    phone: "",
    city: "",
    installationMasterId: "",
    no: "",
    postalCode: "",
}

const newContactPointInitialValue = {
    no: 1,
    name: "",
    surname: "",
    vatNumber: "",
    mobile: "",
    landline: "",
    otherPhone: "",
    email: "",
    contactNotes: "",
    acceptsNotifications: "inactive"
};

const newUserInitialValue = {
    firstName: "",
    lastName: "",
    userName: "",
    password: "",
    email: "",
    lang: "el",
    companies: [],
    primaryCompany: "",
    permissions: [],
    role: "",
    status: 'Active',
    canAccessExpired: 'inactive',
    userType: "User",
    telephoneId: "",
}

const newStockHolderInitialValue = {
    no: "",
    id: "",
    name: "",
    surname: "",
    vat: "",
    position: "",
    percentage: "0",
    stockValue: 0,
    dateFrom: "",
    dateTo: "",
    isActive: "true",
}

const newCompanyInitialValue = {
    companyParternshipStartOn: currentDate,
    companyEstablishedDate: currentDate,
    companyExpireDate: currentDate,
    yearFrom: currentYear,
    name: "",
    smallName: "",
    vatNumber: "",
    doy: "",
    activity: "",
    country: "GR",
    headquartersAddress: "",
    city: "",
    postalCode: "",
    email: "",
    gemhNo: "",
    webpage: "",
    phone: "",
    mobileNo: "",
    contactPoints: [{...newContactPointInitialValue, acceptsNotifications: "true"}],
    users: [],
    companyTemplate: "",
    clientNotes: "",
    myDataApiEnvironment: "production",
    myDataMode: "live",
    myDataApiUserId: "",
    myDataApiSubscriptionKey: "",
    primerMyDataApiEnvironment: "production",
    primerMyDataApiUsername: "",
    primerMyDataApiPassword: "",
    primerMyDataApiSerialNumber: "",
    enableAdminSyncProviderInvoices: "inactive",
    enablePeiraeusTransactions: "inactive",
    importTransactionsFrom: "",
    importTransactionsTo: "",
    primerMyDataApiRemainingDocuments: "",
    primerMyDataApiMonthlyRemainingDocuments: "",
    primerMyDataApiStartDate: "",
    primerMyDataApiEndDate: "",
    installations: [newInstallationInitialValue],
    years: [],
    isActive: "Active",

    // Stocks & Stock holders
    capital: 0,
    sharesOut: 0,
    stockholders: [],

    autofillAccountantRestApi: false,
    automaticBankTransactionsEmailFetching: false,
}

const companyDetails = {
    name: 'Dummy Company Name',
    smallName: "Small Company",
    activity: "Test activity",
    vatNumber: "4654646",
    headquartersAddress: "Test Address",
    city: "Test",
    county: "Test",
    postalCode: "4554",
    phoneNumber: "645646544",
    emailAddress: "test@test.com",
    gemhNo: "3235354",
    webpage: "http://localhost:3000/",
    representativeName: "",
    representativeSurname: "",
    representativeVatNumber: "",
    installations: [{
        no: 1,
        _id: "1",
        type: "Main Address",
        address: "Test Address",
        doy: "Test DOY",
        city: "Test city",
        county: "Test County",
    }, {
        no: 1,
        _id: "2",
        type: "Second Address",
        address: "Test Address",
        doy: "Test DOY",
        city: "Test city",
        county: "Test County",
    }],
    users: [{
        no: 1,
        _id: "1",
        name: "test user1",
        startDate: "01/03/2022",
        endDate: "01/05/2022",
        userRights: "sales, products",
        username: "username1",
    }, {
        no: 1,
        _id: "2",
        name: "test user2",
        startDate: "01/02/2022",
        endDate: "01/08/2022",
        userRights: "sales, products",
        username: "username2",
    }],
    companyLogo: "",
    useLogo: ""
}

const newCategoryInitialValue = {
    _id: 1,
    category: "",
    mask: "CCM-0001",
    maskCode: "",
    maskCodePrefix: "CCM",
    maskCodeCounter: 1,
    isActive: "active",
    type: "client",
};

const initialState = {
    hasErrors: false,
    company: {
        id: '61b87129f622f2333f300aeb',
        name: 'Company 1',
        year: (new Date()).getFullYear().toString()
    },
    searchTerm: "",
    newCompany: newCompanyInitialValue,
    editCompany: {},
    newCompanyInitialValue: newCompanyInitialValue,
    newInstallationInitialValue: newInstallationInitialValue,
    newInstallation: newInstallationInitialValue,
    newUser: newUserInitialValue,
    newUserInitialValue: newUserInitialValue,
    selectedUser: newUserInitialValue,
    newStockHolderInitialValue: newStockHolderInitialValue,
    newContactPointInitialValue: newContactPointInitialValue,
    newContactPoint: newContactPointInitialValue,
    editContactPoint: newContactPointInitialValue,
    newCategory: newCategoryInitialValue,
    companyDetails: companyDetails,
    companyInstallations: [],
    dashboardData: {},
    dashboardBuysData: {},
    newsData: [],
    userSettings: {},
    installations: [],
    stockholders: [],
    contactPoints: [],
    activeModal: null,

    dashboardGraphSettings: {
        graphSetting: "monthly",
        graphMode: 1,
        currentMonth: moment().month(),
    },
}

export const CompanySlice = createSlice({
    name: "company",
    initialState,
    reducers: {
        setCompany: (state, {payload}) => {
            state.company = payload;
            state.hasErrors = false;
        },
        setNewCompany: (state, { payload }) => {
            state.newCompany = payload;
        },
        resetNewCompany: (state) => {
            state.newCompany = state.newCompanyInitialValue;
        },
        setNewContactPoint: (state, { payload }) => {
            state.newContactPoint = payload;
        },
        resetNewInstallations: (state) => {
            state.newInstallation = state.newInstallationInitialValue;
        },
        setSelectedUser: (state, { payload }) => {
            state.selectedUser = payload;
        },
        resetSelectedUser: (state) => {
            state.selectedUser = state.newUserInitialValue;
        },
        setNewInstallations: (state, { payload }) => {
            state.newInstallation = payload;
        },
        setCompanyDetails: (state, {payload}) => {
            state.companyDetails = payload;
            state.hasErrors = false;
        },
        setCompanyInstallations: (state, {payload}) => {
            state.companyInstallations = payload;
            state.loading = false;
            state.hasErrors = false;
        },
        setDashboardData: (state, {payload}) => {
            state.dashboardData = payload;
        },
        updateDashboardData: (state, {payload}) => {
            state.dashboardData = {...state.dashboardData, ...payload};
        },
        setDashboardBuysData: (state, {payload}) => {
            state.dashboardBuysData = payload;
        },
        setNewsData: (state, {payload}) => {
            state.newsData = payload;
        },
        setDashboardGraphSettings: (state, {payload}) => {
            state.dashboardGraphSettings = payload;
        },
        resetDashboardGraphSettings: (state) => {
            state.dashboardData = {};
            state.dashboardBuysData = {};
            state.dashboardGraphSettings = initialState.dashboardGraphSettings;
        },
        setSearchTerm: (state, { payload }) => {
            state.searchTerm = payload;
        },
        setUserSettings: (state, {payload}) => {
            state.userSettings = payload;
        },
        setActiveModal: (state, { payload }) => {
            state.activeModal = payload;
        },
    },
});

export const {
    setCompany,
    setNewCompany,
    resetNewCompany,
    setCompanyDetails,
    setCompanyInstallations,
    setSelectedUser,
    resetSelectedUser,
    updateDashboardData,
    setDashboardData,
    setDashboardBuysData,
    setNewsData,
    resetDashboardGraphSettings,
    resetNewInstallations,
    setNewContactPoint,
    setNewInstallations,
    setDashboardGraphSettings,
    setSearchTerm,
    setUserSettings,
    setActiveModal
} = CompanySlice.actions;
